/* String Furniture planner: scoped to sections that explicitly include it. */
(() => {
  if (window.StringFurniturePlanner) {
    window.StringFurniturePlanner.initAll(document);
    return;
  }

  const plannerOrigin = 'https://planner.stringfurniture.com';
  const instances = new WeakMap();
  const normalize = (value) => String(value ?? '').trim();
  const normalizeSku = (value) => normalize(value).toUpperCase();

  const init = (root) => {
    if (instances.has(root)) return;
    const frame = root.querySelector('[data-string-planner-frame]');
    const status = root.querySelector('[data-string-planner-status]');
    if (!frame) return;

    const setStatus = (message, state = '') => {
      if (!status) return;
      status.textContent = message;
      status.dataset.state = state;
      status.hidden = !message;
    };

    let variants;
    try {
      variants = JSON.parse(root.querySelector('[data-string-planner-products]')?.textContent || '[]');
      if (!Array.isArray(variants)) throw new Error('Invalid product mapping');
    } catch (error) {
      console.error('String Planner product mapping could not be read.', error);
      setStatus('The planner product list could not be loaded. Please contact us for help.', 'error');
      return;
    }

    const variantsBySku = new Map();
    const variantsByEan = new Map();
    const indexVariant = (map, key, variant) => {
      if (!key) return;
      // Duplicate identifiers are unsafe to resolve to an arbitrary product.
      if (map.has(key) && String(map.get(key)?.id) !== String(variant.id)) {
        map.set(key, null);
      } else if (!map.has(key)) {
        map.set(key, variant);
      }
    };
    variants.forEach((variant) => {
      indexVariant(variantsBySku, normalizeSku(variant.sku), variant);
      indexVariant(variantsByEan, normalize(variant.ean), variant);
    });

    const cartUrl = () => window.theme?.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`;
    let adding = false;

    const addToCart = async (payload) => {
      if (adding) return;
      const requestedItems = Array.isArray(payload?.items) ? payload.items : [];
      const itemsById = new Map();
      const unmatched = [];

      for (const item of requestedItems) {
        const article = normalizeSku(item?.article);
        const ean = normalize(item?.ean);
        // Prefer String's exact article number; fall back to EAN if SKU is absent.
        const variant = variantsBySku.has(article)
          ? variantsBySku.get(article)
          : variantsByEan.get(ean);
        const quantity = Number(item?.quantity);
        const id = Number(variant?.id);

        if (!variant || !Number.isSafeInteger(id) || id <= 0) {
          unmatched.push(article || ean || 'unknown component');
          continue;
        }
        if (!Number.isSafeInteger(quantity) || quantity <= 0) {
          setStatus('A component has an invalid quantity. Please review your design and try again.', 'error');
          return;
        }

        if (itemsById.has(id)) {
          itemsById.get(id).quantity += quantity;
          if (!Number.isSafeInteger(itemsById.get(id).quantity)) {
            setStatus('A component quantity is too large. Please review your design.', 'error');
            return;
          }
        } else {
          const properties = {};
          if (payload.designid) properties['_String Planner design ID'] = String(payload.designid);
          if (payload.name) properties['_String Planner design name'] = String(payload.name);
          itemsById.set(id, { id, quantity, properties });
        }
      }

      if (unmatched.length) {
        console.warn('String Planner components missing or ambiguous in the Shopify collection:', unmatched);
        setStatus(`We couldn't match ${unmatched.length} planner component${unmatched.length === 1 ? '' : 's'} to the store. Please contact us for help with this design.`, 'error');
        return;
      }
      const items = Array.from(itemsById.values());
      if (!items.length) {
        setStatus('No purchasable components were found in this design.', 'error');
        return;
      }

      adding = true;
      setStatus('Adding your String configuration to the cart…', 'loading');
      let cartData;
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items,
            sections: 'cart-items,cart-footer,cart-item-count,cart-live-region',
            sections_url: window.location.pathname,
          }),
        });
        cartData = await response.json();
        if (!response.ok) {
          throw new Error(cartData.description || cartData.message || `Cart request failed (${response.status})`);
        }
      } catch (error) {
        console.error('String Planner cart error:', error);
        setStatus(error.message || 'Your configuration could not be added. Please try again.', 'error');
        adding = false;
        return;
      }

      // Shopify sets cart/checkout prices. No planner price is sent to the cart.
      setStatus('Your String configuration was added to the cart.', 'success');
      const sections = cartData.sections;
      const requiredSections = ['cart-items', 'cart-footer', 'cart-item-count', 'cart-live-region'];
      if (!sections || requiredSections.some((key) => typeof sections[key] !== 'string')) {
        window.location.assign(cartUrl());
        return;
      }

      try {
        document.body.dispatchEvent(new CustomEvent('baseline:cart:update', {
          bubbles: true,
          detail: { response: cartData },
        }));
        if (window.theme?.settings?.cartType === 'drawer' && document.querySelector('[data-cart-drawer]')) {
          document.dispatchEvent(new CustomEvent('theme:open:cart-drawer'));
        } else {
          window.location.assign(cartUrl());
        }
      } catch (error) {
        // The add already succeeded; do not invite another add on a UI error.
        console.error('String Planner cart display could not refresh.', error);
        window.location.assign(cartUrl());
      } finally {
        adding = false;
      }
    };

    const messageHandler = (event) => {
      const iframe = frame.querySelector('iframe[src^="https://planner.stringfurniture.com/"]');
      if (event.origin !== plannerOrigin || !iframe || event.source !== iframe.contentWindow) return;
      if (!event.data || typeof event.data !== 'object') return;
      if (event.data.event === 'byoLoaded') {
        if (!variants.length) {
          setStatus('The planner is ready, but its Shopify product collection has not been configured yet.', 'error');
          return;
        }
        event.source.postMessage({ event: 'enableCart', data: {} }, plannerOrigin);
      } else if (event.data.event === 'cartUpdate') {
        void addToCart(event.data.data || {});
      }
    };

    // Install the listener before loading the iframe, including cached loads.
    window.addEventListener('message', messageHandler, true);
    const loader = document.createElement('script');
    loader.async = true;
    loader.src = `${plannerOrigin}/string-planner.js`;
    for (const key of ['planner', 'lan', 'countrycode', 'title', 'height', 'guid']) {
      if (frame.dataset[key]) loader.dataset[key] = frame.dataset[key];
    }
    loader.dataset.minHeight = '730px';
    loader.dataset.minWidth = '960px';
    loader.addEventListener('error', () => {
      setStatus('The String Furniture planner could not load. Please refresh the page or contact us.', 'error');
    });
    instances.set(root, {
      destroy() {
        window.removeEventListener('message', messageHandler, true);
        frame.replaceChildren();
        instances.delete(root);
      },
    });
    frame.appendChild(loader);
  };

  const rootsWithin = (scope) => {
    const roots = Array.from(scope.querySelectorAll('[data-string-planner-root]'));
    if (scope.matches?.('[data-string-planner-root]')) roots.unshift(scope);
    return roots;
  };
  const initAll = (scope) => rootsWithin(scope).forEach(init);
  window.StringFurniturePlanner = { initAll };
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    rootsWithin(event.target).forEach((root) => instances.get(root)?.destroy());
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  } else {
    initAll(document);
  }
})();
